/**
 * API Service Layer - Base configuration and utilities
 */

import { API_URLS, API_BASE_URL, getBaseUrl } from '../config/environment';
import TokenManager from '@/features/authentication/services/tokenManager';

export const SERVICE_URLS = {
  get USER_SERVICE() { return API_URLS.USER_SERVICE; },
  get BOOK_SERVICE() { return API_URLS.BOOK_SERVICE; },
  get PAYMENT_SERVICE() { return API_URLS.PAYMENT_SERVICE; },
  get AI_SERVICE() { return API_URLS.AI_SERVICE; },
  get GAMIFICATION_SERVICE() { return API_URLS.GAMIFICATION_SERVICE; }
};

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  detail?: string;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/** Resolve service upload paths through the API Gateway for web/mobile clients. */
export const resolveMediaUrl = (value: string | null | undefined): string | null | undefined => {
  if (!value || /^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  if (!value.startsWith('/uploads/') && !value.startsWith('uploads/')) return value;
  const baseUrl = getBaseUrl() || (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}`
    : '');
  return `${baseUrl.replace(/\/$/, '')}/${value.replace(/^\//, '')}`;
};

const normalizeMediaUrls = (value: any): any => {
  if (Array.isArray(value)) return value.map(normalizeMediaUrls);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [
    key,
    typeof item === 'string' && (key.includes('cover') || key.includes('icon') || key.includes('image'))
      ? resolveMediaUrl(item)
      : normalizeMediaUrls(item)
  ]));
};

export interface RequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string>;
}

export const setAccessToken = async (
  access_token: string,
  refresh_token: string,
  expires_in: number = 300,
  persistNative: boolean = true
) => {
  const tm = TokenManager.getInstance();
  await tm.setTokens(access_token, refresh_token, expires_in, { persistNative });
};

export const getAccessToken = async (): Promise<string | null> => {
  const tm = TokenManager.getInstance();
  return await tm.getAccessToken();
};

export const forceRefreshAccessToken = async (): Promise<string | null> => {
  const tm = TokenManager.getInstance();
  return await tm.forceRefreshAccessToken();
};

export const clearAccessToken = async () => {
  const tm = TokenManager.getInstance();
  await tm.clearTokens();
};

export class ApiClient {
  private baseUrlGetter: () => string;

  constructor(baseUrlOrGetter: string | (() => string)) {
    this.baseUrlGetter = typeof baseUrlOrGetter === 'function'
      ? baseUrlOrGetter
      : () => baseUrlOrGetter;
  }

  private get baseUrl(): string {
    return this.baseUrlGetter();
  }

  private handleRedirectToLogin(): void {
    if (typeof window === 'undefined') return;

    const rawPath = (typeof window.location.hash === 'string' && window.location.hash.startsWith('#'))
      ? window.location.hash.slice(1)
      : window.location.pathname;
    const path = rawPath || '/';
    const suppress = (window as any).__suppressAuthRedirect === true;
    const authPages = [
      '/welcome',
      '/login',
      '/register',
      '/email-input',
      '/otp-verification',
      '/signup-otp-verification',
      '/signup-success',
      '/platform-discovery',
      '/age-selection',
      '/daily-reading-time',
      '/name-input',
      '/auth'
    ];
    const onAuthPage = authPages.some((p) => path.startsWith(p));
    const skipRedirect = suppress || onAuthPage;

    (window as any).__apiRedirecting = (window as any).__apiRedirecting || false;
    if (!skipRedirect && !(window as any).__apiRedirecting) {
      (window as any).__apiRedirecting = true;
      console.warn('[API] Redirecting to /login due to auth failure');
      window.location.href = '/login';
      setTimeout(() => {
        (window as any).__apiRedirecting = false;
      }, 2000);
    } else {
      console.warn('[API] Skipping redirect to avoid loop');
    }
  }

  private async resolveRefreshFailure(isAuthEndpoint: boolean): Promise<ApiResponse<any>> {
    const tm = TokenManager.getInstance();
    const failureKind = tm.getLastRefreshFailureKind();
    const hasSession = await tm.hasStoredSession();

    if (failureKind === 'auth' || failureKind === 'no_session' || !hasSession) {
      await clearAccessToken();
      if (!isAuthEndpoint) {
        this.handleRedirectToLogin();
      } else {
        console.warn('[API] Skipping redirect on auth endpoint');
      }
      return {
        success: false,
        data: null,
        error: 'Not authenticated'
      };
    }

    return {
      success: false,
      data: null,
      error: 'Session refresh unavailable'
    };
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      params
    } = config;

    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    console.log(`[API] ${method} ${url}`);
    console.log('[API] Request config:', {
      endpoint,
      method,
      hasBody: !!body,
      paramsCount: params ? Object.keys(params).length : 0
    });

    const defaultHeaders: Record<string, string> = {
      Accept: 'application/json'
    };

    try {
      const access_token = await getAccessToken();
      if (access_token) {
        defaultHeaders.Authorization = `Bearer ${access_token}`;
        console.log('[API] Authorization header added');
      } else {
        console.log('[API] No access token available');
      }
    } catch (e) {
      console.warn('[API] Failed to attach auth token:', e);
    }

    try {
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      const mergedHeaders: Record<string, string> = { ...defaultHeaders, ...headers };
      if (!isFormData) {
        mergedHeaders['Content-Type'] = mergedHeaders['Content-Type'] || 'application/json';
      }

      const doFetch = async (): Promise<Response> => {
        const native = typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
        if (native) {
          if (isFormData) {
            return new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.timeout = 30000;
              xhr.open(method, url, true);
              xhr.withCredentials = true;
              Object.entries(mergedHeaders).forEach(([key, value]) => {
                if (key.toLowerCase() !== 'content-type') {
                  xhr.setRequestHeader(key, value);
                }
              });
              xhr.onload = () => {
                try {
                  const responseText = xhr.responseText;
                  let responseData: any = responseText;
                  try { responseData = responseText ? JSON.parse(responseText) : null; } catch {}
                  resolve({
                    ok: xhr.status >= 200 && xhr.status < 300,
                    status: xhr.status,
                    statusText: xhr.statusText || (xhr.status >= 200 && xhr.status < 300 ? 'OK' : 'Error'),
                    json: async () => responseData,
                    text: async () => responseText,
                    headers: new Headers()
                  } as Response);
                } catch (error) {
                  reject(new Error(`Failed to parse response: ${error}`));
                }
              };
              xhr.onerror = () => reject(new Error(`Network error: ${xhr.status} ${xhr.statusText}`));
              xhr.ontimeout = () => reject(new Error('Request timeout'));
              xhr.send(body as FormData);
            });
          }

          try {
            const core = await import('@capacitor/core');
            const CapacitorHttp = (core as any).CapacitorHttp;
            if (!CapacitorHttp?.request) throw new Error('CapacitorHttp not available');

            const capRes = await CapacitorHttp.request({
              url,
              method,
              headers: mergedHeaders,
              // DELETE requests may carry a JSON body (for example batch
              // topic removal). Do not drop it on native Android.
              data: method === 'GET' ? undefined : (typeof body === 'string' ? JSON.parse(body as any) : body),
              connectTimeout: 30000,
              readTimeout: 30000,
            });

            const capData = capRes?.data;
            const asText = typeof capData === 'string' ? capData : JSON.stringify(capData ?? '');
            return {
              ok: capRes?.status >= 200 && capRes?.status < 300,
              status: capRes?.status ?? 0,
              statusText: (capRes?.status >= 200 && capRes?.status < 300) ? 'OK' : 'Error',
              json: async () => (typeof capData === 'string' ? JSON.parse(capData) : capData),
              text: async () => asText,
              headers: new Headers(capRes?.headers || {}),
            } as Response;
          } catch (e) {
            console.warn('[API] CapacitorHttp unavailable or failed, falling back to XHR/fetch:', e);
            return new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.timeout = 30000;
              xhr.open(method, url, true);
              xhr.withCredentials = true;
              xhr.setRequestHeader('Accept', 'application/json');
              xhr.onload = () => {
                try {
                  const responseText = xhr.responseText;
                  let responseData: any = responseText;
                  try { responseData = responseText ? JSON.parse(responseText) : null; } catch {}
                  resolve({
                    ok: xhr.status >= 200 && xhr.status < 300,
                    status: xhr.status,
                    statusText: xhr.statusText || (xhr.status >= 200 && xhr.status < 300 ? 'OK' : 'Error'),
                    json: async () => responseData,
                    text: async () => responseText,
                    headers: new Headers()
                  } as Response);
                } catch (error) {
                  reject(new Error(`Failed to parse response: ${error}`));
                }
              };
              xhr.onerror = () => reject(new Error(`Network error: ${xhr.status} ${xhr.statusText}`));
              xhr.ontimeout = () => reject(new Error('Request timeout'));
              if (!body || method === 'GET') xhr.send();
              else xhr.send(typeof body === 'string' ? body : JSON.stringify(body));
            });
          }
        }

        return fetch(url, {
          method,
          headers: mergedHeaders,
          body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
          credentials: 'include'
        });
      };

      let response = await doFetch();
      console.log(`[API] Response status: ${response.status} ${response.statusText}`);

      if (response.status === 401) {
        console.warn('[API] 401 received, attempting token refresh...');

        const isUserService = this.baseUrl === SERVICE_URLS.USER_SERVICE;
        const isAuthEndpoint = endpoint.startsWith('/auth/');

        if (isUserService) {
          try {
            const fresh_token = await forceRefreshAccessToken();
            if (fresh_token) {
              mergedHeaders.Authorization = `Bearer ${fresh_token}`;
              console.log('[API] Retrying request with refreshed token');
              response = await doFetch();
            } else {
              return await this.resolveRefreshFailure(isAuthEndpoint) as ApiResponse<T>;
            }
          } catch (e) {
            console.error('[API] Refresh failed for user-service');
            return await this.resolveRefreshFailure(isAuthEndpoint) as ApiResponse<T>;
          }
        } else {
          try {
            console.log('[API] Non-user service 401, attempting refresh via user-service');
            const fresh_token = await forceRefreshAccessToken();
            if (fresh_token) {
              mergedHeaders.Authorization = `Bearer ${fresh_token}`;
              console.log('[API] Retrying non-user service request with refreshed token');
              response = await doFetch();
            } else {
              return await this.resolveRefreshFailure(isAuthEndpoint) as ApiResponse<T>;
            }
          } catch (e) {
            console.error('[API] Refresh failed for non-user service');
            return await this.resolveRefreshFailure(isAuthEndpoint) as ApiResponse<T>;
          }
        }
      }

      if (!response.ok) {
        let errorData: any = null;
        try {
          errorData = await response.json();
        } catch {}

        // FastAPI may return detail as either a string or an object. Extract
        // the actual text so callers never receive "[object Object]".
        const errorMessage = errorData?.detail?.debug
          || errorData?.detail?.message
          || (typeof errorData?.detail === 'string' ? errorData.detail : undefined)
          || errorData?.message
          || `HTTP ${response.status}: ${response.statusText}`;
        console.error('[API] HTTP Error:', errorMessage);
        console.error('[API] Error data:', errorData);

        return {
          success: false,
          data: errorData as T,
          error: errorMessage,
          detail: errorData?.detail
        };
      }

      const data = await response.json();
      console.log('[API] Response data received:', {
        hasData: !!data,
        message: data.message,
        dataType: Array.isArray(data) ? 'array' : typeof data
      });

      return {
        success: true,
        data: normalizeMediaUrls(data),
        message: data.message
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error(`[API] Error in ${method} ${url}:`, error);
      console.error('[API] Error details:', {
        url,
        method,
        message: errorMessage,
        stack: error instanceof Error ? error.stack : undefined
      });

      return {
        success: false,
        data: null as T,
        error: errorMessage
      };
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  async delete<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', body });
  }
}

export const userServiceApi = new ApiClient(() => SERVICE_URLS.USER_SERVICE);
export const bookServiceApi = new ApiClient(() => SERVICE_URLS.BOOK_SERVICE);
export const paymentServiceApi = new ApiClient(() => SERVICE_URLS.PAYMENT_SERVICE);
export const aiServiceApi = new ApiClient(() => SERVICE_URLS.AI_SERVICE);
export const gamificationServiceApi = new ApiClient(() => SERVICE_URLS.GAMIFICATION_SERVICE);

export default new ApiClient(() => API_BASE_URL);
